import { ReactNode, useState } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Alignable,
    Box,
    Column,
    Columns,
    ColumnsSize,
    Icon,
    IconName,
    IconSize,
    Price,
    PriceLevel,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { MarketLineType } from '@app-types/contract';

import { MoreContracts } from '@components/connection/more-contracts';

export interface LineType {
    label: string;
    discount?: number;
    marketLine: MarketLineType;
}

export enum LineTypeEnum {
    PROPOSED = 'proposed',
    ELIGIBLE = 'eligible',
}

export interface LinesProps {
    type: LineTypeEnum;
    label?: ReactNode;
    lines: LineType[];
    mainOfferLabel?: string;
    step: number;
    showStep?: boolean;
    maxLinesToShow?: number;
}

export function Lines({
    type,
    step,
    showStep = false,
    label,
    mainOfferLabel,
    lines = [],
    maxLinesToShow = 2,
}: Readonly<LinesProps>) {
    const [shouldShowMore, setShouldShowMore] = useState(false);

    return (
        <Column data-cy={`${type}-discount`}>
            {label && (
                <>
                    <View flexable align={Alignable.ALIGNED_CENTER} className='gap-medium'>
                        {showStep ? (
                            <Text level={TextLevels.TWO} typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD]}>
                                {step}. {label}
                            </Text>
                        ) : (
                            <Text level={TextLevels.TWO} typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD]}>
                                <Icon
                                    size={IconSize.MEDIUM}
                                    name={
                                        lines?.[0]?.marketLine !== MarketLineType.FNB
                                            ? IconName.SIM_CARD
                                            : IconName.BOX_MOBILE
                                    }
                                />
                                {label}
                            </Text>
                        )}
                    </View>
                    <Spacer size={SpacerSize.TWO} />
                </>
            )}
            <Columns mobile multiline className='gap-medium'>
                {lines.slice(0, lines.length > maxLinesToShow ? maxLinesToShow - 1 : maxLinesToShow).map((line) => (
                    <Line type={type} key={line.label} {...line} />
                ))}
                {lines.length > maxLinesToShow && (
                    <Column size={12} align={Alignable.ALIGNED_CENTER}>
                        <Text level={TextLevels.TWO}>
                            <button
                                type='button'
                                className='is-centered button-clear link'
                                onClick={() => setShouldShowMore(true)}
                            >
                                Voir tous mes forfaits
                            </button>
                        </Text>
                    </Column>
                )}
            </Columns>
            {shouldShowMore && (
                <MoreContracts lines={lines} mainOfferLabel={mainOfferLabel} onClose={() => setShouldShowMore(false)} />
            )}
        </Column>
    );
}

interface LineProps extends LineType {
    type: LineTypeEnum;
    size?: ColumnsSize;
}

export function Line({ type = LineTypeEnum.PROPOSED, label, discount, size = 12 }: Readonly<LineProps>) {
    let className = 'is-dashed';
    if (type === LineTypeEnum.ELIGIBLE) {
        className = 'has-background-grey-fade is-flat';
    }

    return (
        <Column size={size}>
            <Box
                shadowless
                fullheight
                className={classNames('is-flex-row is-aligned-center is-spaced-between is-narrow', className)}
            >
                <Text level={TextLevels.TWO} marginless typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                    {label}
                </Text>
                {discount && <Price level={PriceLevel.SIX} amount={discount} period='mois' hideCents={false} />}
            </Box>
        </Column>
    );
}
